import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, concat, finalize, Observable, of, Subject, take, tap, throwError, timer } from 'rxjs';
import { AssetLiabilitiesModel, AusResponse, AusValidationIssue, BorrowerModel, Cmg1003Model, LoanDataModel, LoanStatus } from '../cmg-1003/models';
import { softLockStatuses } from '../core/state';
import { SpinnerService } from '../shared/services/spinner.service';
import {
  AusErrorLogModel,
  AusLoanModel,
  CommunityLendingModel,
  DesktopUnderwritingSystemTypes,
  FhaAusType,
  OrderLogModel,
  VerificationServiceModel,
} from './models';
import { InternetDisconnectService } from '../services/internet-disconnect.service';
import { REQUIRES_SECURITY_PROFILE } from '../interceptors';
import { AusStore } from '../loan-state';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

@Injectable({ providedIn: 'root' })
export class AusPipelineService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _environment = inject(APP_ENV);
  private readonly _messageService = inject(MessageService);
  private readonly _internetDisconnectService = inject(InternetDisconnectService);
  private readonly _ausStore = inject(AusStore);

  private _all1003: Cmg1003Model;
  private _all1003Pristine: Cmg1003Model;
  private _ausLoanInfo$ = new BehaviorSubject<AusLoanModel>(null);
  private _fhaAus$ = new BehaviorSubject<{ fhaAusType: FhaAusType }>(null);
  private _loanInfo$ = new BehaviorSubject<LoanDataModel>(null);
  private _loanStatus$ = new BehaviorSubject<LoanStatus>(null);
  private _borrowers$ = new BehaviorSubject<BorrowerModel[]>(null);
  private _fileDataId$ = new BehaviorSubject<number>(null);
  private _orderLogs$ = new BehaviorSubject<OrderLogModel[]>([]);
  private _errorLogs$ = new BehaviorSubject<AusErrorLogModel[]>([]);
  private _isDirty$ = new BehaviorSubject<boolean>(null);
  private _isAusEnabled$ = new BehaviorSubject<boolean>(null);

  get isDirty$(): Observable<boolean> {
    return this._isDirty$.asObservable();
  }

  get orderLogs$(): Observable<OrderLogModel[]> {
    return this._orderLogs$.asObservable();
  }

  get errorLogs$(): Observable<AusErrorLogModel[]> {
    return this._errorLogs$.asObservable();
  }

  get all1003(): Cmg1003Model {
    return this._all1003;
  }

  get ausLoanInfo$(): Observable<AusLoanModel> {
    return this._ausLoanInfo$.asObservable();
  }

  get fhaAus$(): Observable<any> {
    return this._fhaAus$.asObservable();
  }

  get loanInfo$(): Observable<LoanDataModel> {
    return this._loanInfo$.asObservable();
  }

  get loanStatus$(): Observable<LoanStatus> {
    return this._loanStatus$.asObservable();
  }

  get borrowers$(): Observable<BorrowerModel[]> {
    return this._borrowers$.asObservable();
  }

  set all1003(v: Cmg1003Model) {
    this._all1003Pristine = this._all1003 = v;
    this._ausLoanInfo$.next(v?.aus?.loanInformation);
    this._fhaAus$.next({ fhaAusType: v?.aus.fhaAusType });
    this._loanInfo$.next(v?.loanInformation);
    this._loanStatus$.next(v?.loanStatusTypeId);
    this._borrowers$.next(v?.borrowers);
    this._fileDataId$.next(this._all1003.fileDataId);
    this._orderLogs$.next(this._all1003.aus.orderLogs);
    this._errorLogs$.next(this._all1003.aus.errorLogs);
    this._isAusEnabled$.next(softLockStatuses.includes(v?.loanStatusTypeId));
    this._isDirty$.next(false);
  }

  get isAusEnabled$(): Observable<boolean> {
    return this._isAusEnabled$.asObservable();
  }

  get fileDataId$(): Observable<number> {
    return this._fileDataId$.asObservable();
  }

  /**
   * runAusInPipeline - Due to store not available in pipeline, this function runs aus on 1003 model.
   * @param ausSystem
   * @param fileDataId
   * @param creditReferenceNumber
   * @param onAusSuccessFn
   * @param onError
   */
  runAus(ausSystem: DesktopUnderwritingSystemTypes, fileDataId: number, creditReferenceNumber: string) {
    this.isDirty$.pipe(take(1)).subscribe((isAusDirty) => {
      if (isAusDirty) {
        this.saveAus()
          .pipe(take(1))
          .subscribe((result) => {
            if (result) {
              this._runAus(ausSystem, fileDataId, creditReferenceNumber);
            }
          });
      } else {
        this._runAus(ausSystem, fileDataId, creditReferenceNumber);
      }
    });
  }

  private _runAus(ausSystem: DesktopUnderwritingSystemTypes, fileDataId: number, creditReferenceNumber: string) {
    const payload = {
      fileDataId: fileDataId,
      creditReferenceNumber: creditReferenceNumber,
    };
    const lpaEndpoint = this._httpClient.post(`${this._environment.retail.apiUrl}/aus/orderloanprospector`, payload);
    const duEndpoint = this._httpClient.post(`${this._environment.retail.apiUrl}/aus/orderdesktopunderwriter`, payload);
    const duEaEndpoint = this._httpClient.post(`${this._environment.retail.apiUrl}/aus/orderduearlyassessment`, payload);
    this._errorLogs$.next([]);

    this._spinnerService.show();
    switch (ausSystem) {
      case DesktopUnderwritingSystemTypes.DU:
        duEndpoint.pipe(take(1), catchError(this._handleError)).subscribe({
          next: (ausResponse) => {
            this._onAusSuccess(ausResponse as AusResponse);
          },
          complete: () => {
            this._spinnerService.hide();
          },
        });
        break;
      case DesktopUnderwritingSystemTypes.LPA:
        lpaEndpoint.pipe(take(1), catchError(this._handleError)).subscribe({
          next: (ausResponse) => {
            this._onAusSuccess(ausResponse as AusResponse);
          },
          complete: () => {
            this._spinnerService.hide();
          },
        });
        break;
      case DesktopUnderwritingSystemTypes.Dual_DU_LPA:
        concat(
          duEndpoint.pipe(
            take(1),
            tap((ausResponse) => {
              if (ausResponse) {
                this._onAusSuccess(ausResponse as AusResponse);
              }
            }),
            catchError((duError) => {
              this._handleError(duError);
              this._spinnerService.show();
              return of(null); // Continue even if duEndpoint fails
            }),
          ),
          timer(1000), // 1 second delay to differentiate timestamps in history
          lpaEndpoint.pipe(
            take(1),
            tap((lpaResponse) => {
              if (lpaResponse) {
                this._onAusSuccess(lpaResponse as AusResponse);
              }
              this._spinnerService.hide();
            }),
            catchError((lpaError) => {
              this._handleError(lpaError);
              this._spinnerService.hide();
              return of(null);
            }),
          ),
        )
          .pipe(
            finalize(() => {
              this._spinnerService.hide();
            }),
          )
          .subscribe();
        break;
      case DesktopUnderwritingSystemTypes.EA:
        duEaEndpoint.pipe(take(1), catchError(this._handleError)).subscribe({
          next: (ausResponse) => {
            this._onAusSuccess(ausResponse as AusResponse);
          },
          complete: () => {
            this._spinnerService.hide();
          },
        });
        break;
      default:
        this._spinnerService.hide();
        break;
    }
  }

  reset() {
    this.all1003 = this._all1003Pristine;
  }

  addValidations(system: DesktopUnderwritingSystemTypes, validationIssues: AusValidationIssue[]) {
    let index = 0;
    const errorLogs: AusErrorLogModel[] = [];
    (validationIssues || []).forEach((t) => {
      errorLogs.push({
        id: --index,
        title: '',
        message: (t.category ? `${t.category}: ` : '') + (!t.message ? t.fieldName : t.message),
        system: system === 1 || system === 3 ? 'DU' : 'LPA',
      });
    });
    this._all1003.aus.errorLogs = [...this._all1003.aus.errorLogs, ...errorLogs];
    this._errorLogs$.next(this._all1003.aus.errorLogs);
  }

  addErrors(errors: string[]) {
    let index = 0;
    const errorLogs: AusErrorLogModel[] = [];
    (errors || []).forEach((t) => {
      errorLogs.push({
        id: --index,
        title: '',
        message: t,
        system: 'DU',
      });
    });
    this._all1003.aus.errorLogs = errorLogs;
    this._errorLogs$.next(this._all1003.aus.errorLogs);
  }

  addOrderLog(orderLog: OrderLogModel) {
    this._ausStore.addOrderLog(orderLog);
    this._all1003.aus.orderLogs = [...this._all1003.aus.orderLogs, orderLog];
    this._orderLogs$.next(this._all1003.aus.orderLogs);
  }

  downloadOrderLog(fileDataId: number, documentId: number) {
    const orderlogDocEndpointCall$ = this._httpClient.get(`${this._environment.retail.apiUrl}/document/${fileDataId}/${documentId}`);
    return orderlogDocEndpointCall$.pipe(catchError(this._handleError));
  }

  updateLoanInfo(loanInfo: any, monthlyMcc: number) {
    this._all1003.aus.loanInformation = Object.assign({}, loanInfo);
    this._all1003.loanInformation.monthlyMCC = monthlyMcc;
    this._isDirty$.next(true);
  }

  updateDownpayment(downPayment: any) {
    this._all1003.aus.downPayment = Object.assign({}, downPayment);
    this._isDirty$.next(true);
  }

  updatePropertyInfo(propertyInfo: any) {
    this._all1003.aus.propertyInformation = Object.assign({}, propertyInfo);
    this._isDirty$.next(true);
  }

  updateCommunityLending(communityLending: CommunityLendingModel) {
    this._all1003.aus.communityLending = Object.assign({}, communityLending);
    this._isDirty$.next(true);
  }

  updateGovtLoanForm(govtLoan: any) {
    this._all1003.aus.governmentLoans = Object.assign({}, govtLoan);
    this._isDirty$.next(true);
  }

  addorUpdateVerificationService(vsModel: VerificationServiceModel) {
    const i = this._all1003.aus.verificationServices.findIndex((_element) => _element.serviceOrderId === vsModel.serviceOrderId);
    i > -1 ? (this._all1003.aus.verificationServices[i] = vsModel) : this._all1003.aus.verificationServices.push(vsModel);
    this._isDirty$.next(true);
  }

  getAllReos() {
    const allReos = [];
    this._all1003.assetsLiabilities.forEach((item) => allReos.push(...(item['realEstateOwnedList'] || [])));
    return allReos;
  }

  getExpectedFreddieCashReserves() {
    const cashToClose = this._all1003.transactionInfo.cashToClose > 0 ? this._all1003.transactionInfo.cashToClose : 0;
    let totalAllLiquidAssets = 0;
    let otherAssets = 0;
    (this._all1003.assetsLiabilities || []).forEach((asset) => {
      otherAssets += this._calculateTotalOtherAssets(asset);
      totalAllLiquidAssets += this._calculateTotalBankingAssets(asset);
    });
    return totalAllLiquidAssets + otherAssets - cashToClose;
  }

  saveAus() {
    const ausSaveSuccess$ = new Subject<boolean>();
    this._spinnerService.show();
    const updateEndpoint = `${this._environment.retail.apiUrl}/all1003`;

    // Note: Parts of the payload is borrower-specific.
    const all1003Payload = {
      fileDataId: this._all1003.fileDataId,
      fileData: this._all1003.fileData,
      loanInformation: this._all1003.loanInformation,
      aus: this._all1003.aus,
      property: this._all1003.property, // need this to save AUS->TotalMortgagedProperties
    };

    this._httpClient
      .patch(updateEndpoint, all1003Payload, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      })
      .pipe(catchError(this._handleSaveError))
      .subscribe((all1003Model: Cmg1003Model) => {
        if (all1003Model !== null) {
          this.all1003 = all1003Model;
          this._isDirty$.next(false);
          ausSaveSuccess$.next(true);
        } else {
          this._messageService.add({ severity: 'error', summary: '', detail: 'Unable to save AUS!' });
          ausSaveSuccess$.next(false);
        }
        this._spinnerService.hide();
      });
    return ausSaveSuccess$.asObservable();
  }

  private readonly _handleError = (httpErrorResponse: any) => {
    this._spinnerService.hide();
    // Internet disconnected. Show error modal and do not update error log
    if (this._internetDisconnectService.isInternetDisconnected(httpErrorResponse?.status)) {
      this._internetDisconnectService.showInternetDisconnectModal();
      return;
    }

    let errors = httpErrorResponse.error?.Errors || httpErrorResponse.error?.errors;
    const error = httpErrorResponse.error;

    if (!errors && error) {
      errors = [];
      if (error?.Detail) {
        const detail = JSON.parse(error?.Detail);
        if (detail?.errors) {
          for (const key in detail?.errors) {
            errors[key] = detail?.errors[key];
          }
        }
      } else if (error?.Title) {
        errors['error'] = [error?.Title];
      } else {
        errors['error'] = ['An unknown error has occurred.'];
      }
    }

    if (errors) {
      let systemSource: 'DU' | 'LPA' | 'DU EA';
      if (httpErrorResponse.url.indexOf('orderdesktopunderwriter') > 0) {
        systemSource = 'DU';
      } else if (httpErrorResponse.url.indexOf('orderduearlyassessment') > 0) {
        systemSource = 'DU EA';
      } else {
        systemSource = 'LPA';
      }
      this._updateErrorLogs(systemSource, errors);
    } else {
      this._errorLogs$.next([]);
    }
    let errorMessage = '';
    if (httpErrorResponse.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = httpErrorResponse.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${httpErrorResponse.status}\nMessage: ${httpErrorResponse.message}`;
    }
    return throwError(errorMessage);
  };

  private _updateErrorLogs(system: 'DU' | 'LPA' | 'DU EA', errors) {
    let index = 0;
    const errorLogs = [];
    for (const key in errors) {
      if (errors && Array.isArray(errors[key])) {
        errors[key].forEach((e) => {
          errorLogs.push({ id: index++, system: system, message: e, title: key });
        });
      }
    }
    this._errorLogs$.next([...this._errorLogs$.value, ...(errorLogs || [])]);
  }

  private _handleSaveError() {
    return of(null);
  }

  private _onAusSuccess(ausResponse: AusResponse) {
    if (ausResponse.orderLog) {
      this.addOrderLog(ausResponse.orderLog);

      this._fhaAus$.pipe(take(1)).subscribe((fhaAus) => {
        if (fhaAus) {
          if (ausResponse.system === DesktopUnderwritingSystemTypes.DU) {
            this._fhaAus$.next({ fhaAusType: FhaAusType.DU });
          } else if (ausResponse.system === DesktopUnderwritingSystemTypes.LPA) {
            this._fhaAus$.next({ fhaAusType: FhaAusType.LPA });
          }
        }
      });

      this._httpClient
        .get<Cmg1003Model>(`${this._environment.retail.apiUrl}/all1003/file/${this._all1003.fileDataId}`, {
          context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
        })
        .pipe(catchError(() => of(null)))
        .subscribe((all1003Model) => {
          if (all1003Model?.aus) {
            this._ausLoanInfo$.next(all1003Model.aus.loanInformation);
            this._orderLogs$.next(all1003Model.aus.orderLogs);
            this._all1003.aus.loanInformation = all1003Model.aus.loanInformation;
            this._all1003.aus.orderLogs = all1003Model.aus.orderLogs;
            this._ausStore.update({
              loanInformation: all1003Model.aus.loanInformation,
              orderLogs: all1003Model.aus.orderLogs,
            });
          }
        });
    } else {
      if (ausResponse.errors) {
        this.addErrors(ausResponse.errors);
      }
      if (ausResponse.validationIssues) {
        this.addValidations(ausResponse.system, ausResponse.validationIssues);
      }
    }
  }

  private _calculateTotalBankingAssets(a: AssetLiabilitiesModel) {
    return a.assetsList?.reduce(
      (accumlator, t) => accumlator + (t.checkingSavings || []).reduce((csAcc, cs) => cs.balanceOrMarketValue + csAcc, 0),
      0,
    );
  }

  private _calculateTotalOtherAssets(a: AssetLiabilitiesModel) {
    let total = 0;
    const otherAssetsModel = a.assetsList?.find((t) => t.assetType === 4 && t.isDeleted === false);
    if (otherAssetsModel) {
      total = otherAssetsModel.other?.reduce((accumlator, t) => accumlator + t.balanceOrMarketValue, 0);
    }
    return total;
  }
}
